import { useState } from 'react'
import { Box, Paper, TextField, Button, Typography, Alert } from '@mui/material'
import { useAuth } from '@/contexts/AuthContext'
import { useNavigate } from 'react-router-dom'

const Login = () => {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [code, setCode] = useState('')
  const [error, setError] = useState('')
  const [requires2FA, setRequires2FA] = useState(false)
  const [userId, setUserId] = useState<string | undefined>()
  const [isLoading, setIsLoading] = useState(false)
  const { login, verify2FA } = useAuth()
  const navigate = useNavigate()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setIsLoading(true)
    try {
      if (requires2FA && userId) {
        await verify2FA(userId, code)
        navigate('/')
      } else {
        const result = await login(email, password)
        if (result.requires2FA) {
          setRequires2FA(true)
          setUserId(result.userId)
        } else {
          navigate('/')
        }
      }
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Login failed')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        backgroundColor: 'background.default',
      }}
    >
      <Paper sx={{ p: 4, width: '100%', maxWidth: 400 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
          <img src="/logo.png" alt="Logo" style={{ maxWidth: '200px', height: 'auto' }} />
        </Box>
        <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 3 }}>
          {requires2FA ? 'Enter verification code' : 'Sign in to continue'}
        </Typography>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {requires2FA && (
          <Alert severity="info" sx={{ mb: 2 }}>
            A verification code has been sent to your email. Please check your inbox.
          </Alert>
        )}
        <form onSubmit={handleSubmit}>
          {!requires2FA ? (
            <>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                margin="normal"
                required
                disabled={isLoading}
              />
              <TextField
                fullWidth
                label="Password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                margin="normal"
                required
                disabled={isLoading}
              />
            </>
          ) : (
            <TextField
              fullWidth
              label="Verification Code"
              type="text"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              margin="normal"
              required
              disabled={isLoading}
              placeholder="000000"
              inputProps={{ maxLength: 6, style: { textAlign: 'center', fontSize: '24px', letterSpacing: '8px' } }}
              helperText="Enter the 6-digit code sent to your email"
            />
          )}
          <Button type="submit" fullWidth variant="contained" sx={{ mt: 3 }} disabled={isLoading}>
            {requires2FA ? 'Verify Code' : 'Sign In'}
          </Button>
          {requires2FA && (
            <Button
              fullWidth
              variant="text"
              sx={{ mt: 1 }}
              onClick={() => {
                setRequires2FA(false)
                setCode('')
                setError('')
              }}
              disabled={isLoading}
            >
              Back to Login
            </Button>
          )}
        </form>
        <Typography variant="caption" color="text.secondary" align="center" sx={{ mt: 3, display: 'block' }}>
          CRM created by Alexandru Dinca
        </Typography>
      </Paper>
    </Box>
  )
}

export default Login
